import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import { validateEmailRealTime } from '../utils/validation';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailValidation, setEmailValidation] = useState({ isValid: true, errors: [] });
  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Validate email in real-time
    if (value.trim()) {
      const validation = validateEmailRealTime(value);
      setEmailValidation(validation);
    } else {
      setEmailValidation({ isValid: true, errors: [] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailValidation.isValid) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.forgotPassword(email);
      
      // Store email in session storage for reset password page
      sessionStorage.setItem('resetEmail', email);
      
      toast.success('Password reset code sent to your email');
      
      // Redirect to reset password page
      navigate('/reset-password');
      
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container-split">
      {/* Left Side - Gradient with Reset Theme */}
      <div className="login-gradient-side">
        <div className="login-gradient-content">
          <div className="login-brand-icon">
            <img 
              src="/assets/logo.png" 
              alt="OUK Logo" 
              className="gradient-logo"
            />
          </div>
          <div style={{ textAlign: 'center', color: 'white', marginTop: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px' }}>
              🔐 Reset Your Password
            </h2>
            <p style={{ fontSize: '16px', opacity: 0.9, lineHeight: '1.6' }}>
              Enter your email address and we'll send you a secure reset code 
              to create a new password.
            </p>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '12px', 
              padding: '20px', 
              marginTop: '30px',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>How it works:</h3>
              <ul style={{ textAlign: 'left', fontSize: '14px', opacity: 0.9 }}>
                <li style={{ marginBottom: '8px' }}>Enter your registered email</li>
                <li style={{ marginBottom: '8px' }}>Receive a 6-digit reset code</li>
                <li style={{ marginBottom: '8px' }}>Enter the code to verify</li>
                <li>Create your new secure password</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="login-gradient-shapes">
          <div className="gradient-shape shape-1"></div>
          <div className="gradient-shape shape-2"></div>
          <div className="gradient-shape shape-3"></div>
          <div className="gradient-shape shape-4"></div>
        </div>
      </div>

      {/* Right Side - Forgot Password Form */}
      <div className="login-form-side">
        <div className="login-form-container">
          <div className="login-form-header">
            <div className="login-form-icon">
              <img 
                src="/assets/logo.png" 
                alt="OUK Logo" 
                className="login-logo"
              />
            </div>
            <h2 className="login-form-title">Forgot Password</h2>
            <p style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', marginTop: '8px' }}>
              Enter your email address to receive a password reset code
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  className={`login-input ${!emailValidation.isValid ? 'email-input-invalid' : emailValidation.isValid && email.trim() ? 'email-input-valid' : ''}`}
                  placeholder="Enter your registered email address"
                  value={email}
                  onChange={handleEmailChange}
                  required
                />
                {email.trim() && (
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '16px'
                  }}>
                    {emailValidation.isValid ? (
                      <span style={{ color: '#10b981' }}>✓</span>
                    ) : (
                      <span style={{ color: '#ef4444' }}>✗</span>
                    )}
                  </div>
                )}
              </div>
              {!emailValidation.isValid && email.trim() && (
                <div className="email-validation-errors" style={{ marginTop: '6px' }}>
                  {emailValidation.errors.map((error, index) => (
                    <div key={index} className="email-error-message">
                      <span style={{ color: '#ef4444', fontSize: '12px', marginRight: '4px' }}>⚠</span>
                      <span style={{ color: '#ef4444', fontSize: '12px' }}>{error}</span>
                    </div>
                  ))}
                  {emailValidation.suggestion && (
                    <div className="email-suggestion">
                      <span style={{ color: '#f59e0b', fontSize: '12px', marginRight: '4px' }}>💡</span>
                      <span style={{ color: '#f59e0b', fontSize: '12px' }}>
                        Did you mean: <strong>{emailValidation.suggestion}</strong>?
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <button 
              type="submit" 
              className="login-submit-btn" 
              disabled={loading || !email.trim() || !emailValidation.isValid}
              style={{
                opacity: (loading || !email.trim() || !emailValidation.isValid) ? 0.6 : 1
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>🔄</span>
                  <span>Sending Reset Code...</span>
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>📧</span>
                  <span>Send Reset Code</span>
                </span>
              )}
            </button>

            <div className="forgot-password" style={{ textAlign: 'center', marginTop: '20px' }}>
              <Link to="/login" className="forgot-password-link">
                ← Back to Login
              </Link>
            </div>
          </form>

          <div style={{ 
            textAlign: 'center', 
            marginTop: '20px', 
            padding: '16px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
              🛡️ <strong>Security Notice:</strong> Reset codes expire in 10 minutes for your security. 
              If you don't receive an email, please check your spam folder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;